import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  useTheme,
  Card,
  CardContent,
  Divider,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  CircularProgress,
  Grid,
  Tooltip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  getUserProfile, 
  UserProfile, 
  getTickets, 
  getCustomer, 
  Customer, 
  getCustomerApps, 
  CustomerApp, 
  getCustomerAddresses, 
  CustomerAddress 
} from '../firebase/services';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import AppsIcon from '@mui/icons-material/Apps';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WebIcon from '@mui/icons-material/Web';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Link } from 'react-router-dom';

// Type pour les applications
interface Application {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'pending' | 'expired' | 'inactive' | 'development' | 'maintenance';
  lastUsed?: string;
  icon: React.ReactNode;
  urlApp?: string;
  category?: string;
  techStack?: string;
  stacks?: string[];
  link?: string;
  logoUrl?: string;
  downloads?: PlatformDownload[];
}

// Type pour les informations de facturation
interface Billing {
  currentPlan: string;
  nextBilling: string;
  amount: string;
  status: string;
}

// Type pour les tickets
interface TicketStats {
  active: number;
  resolved: number;
  pending: number;
  closed: number;
  total: number;
}

// Type pour les tickets
interface Ticket {
  id: string;
  ticketId: string;
  subject: string;
  description: string;
  status: 'new' | 'inProgress' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  createdAt: any;
  updatedAt: any;
  userId: string;
  messages?: any[];
}

// Ajouter l'interface PlatformDownload
interface PlatformDownload {
  platform: 'ios' | 'android' | 'windows' | 'linux' | 'web';
  downloadUrl: string;
  version: string;
  status: 'active' | 'deprecated' | 'beta' | 'development';
  updatedAt?: any;
}

// Étendre l'interface CustomerApp pour inclure les nouvelles propriétés
interface ExtendedCustomerApp extends CustomerApp {
  logoUrl?: string;
  stacks?: string[];
  downloads?: PlatformDownload[];
}

const DashboardClient: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [billing, setBilling] = useState<Billing | null>(null);
  const [ticketStats, setTicketStats] = useState<TicketStats | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);

  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  // Mise à jour de la fonction isImageUrl pour vérifier si une URL est une image
  const isImageUrl = (url: string): boolean => {
    if (!url) return false;
    
    // Vérifier les extensions d'image communes
    const hasImageExtension = /\.(jpg|jpeg|png|gif|bmp|svg|webp|ico)$/i.test(url);
    
    // Vérifier les services d'hébergement d'images ou les chemins courants
    const isImageHosting = url.includes('image-heberg') || 
                         url.includes('imgur') ||
                         url.includes('cloudinary') ||
                         url.includes('images') ||
                         url.includes('img') ||
                         url.includes('logo') ||
                         url.includes('icon') ||
                         url.includes('avatar') ||
                         url.includes('photo');
    
    return hasImageExtension || isImageHosting;
  };

  // Fonction pour obtenir le logo de la plateforme
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'ios':
        return '/resources/logo/applestore.png';
      case 'android':
        return '/resources/logo/playstore.png';
      case 'windows':
        return '/resources/logo/windows.png';
      case 'linux':
        return '/resources/logo/linux.png';
      case 'web':
        return '/resources/logo/web.png';
      default:
        return '/resources/logo/web.png';
    }
  };

  // Fonction pour obtenir la couleur selon le statut
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'expired':
        return 'error';
      case 'inactive':
        return 'default';
      case 'development':
        return 'info';
      case 'maintenance':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Fonction pour obtenir le libellé complet du statut
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return getTranslationString('common.active', 'Active');
      case 'pending':
        return getTranslationString('common.pending', 'Pending');
      case 'expired':
        return getTranslationString('common.expired', 'Expired');
      case 'inactive':
        return getTranslationString('common.inactive', 'Inactive');
      case 'development':
        return getTranslationString('common.development', 'Development');
      case 'maintenance':
        return getTranslationString('common.maintenance', 'Maintenance');
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Calcul des statistiques de tickets
  const calculateTicketStats = (userTickets: Ticket[]): TicketStats => {
    return {
      active: userTickets.filter(t => !['resolved', 'closed'].includes(t.status)).length,
      resolved: userTickets.filter(t => t.status === 'resolved').length,
      pending: userTickets.filter(t => t.status === 'pending').length,
      closed: userTickets.filter(t => t.status === 'closed').length,
      total: userTickets.length
    };
  };

  // Récupère le ticket le plus récent
  const getRecentTickets = (userTickets: Ticket[]): Ticket[] => {
    return [...userTickets]
      .sort((a, b) => {
        // Tri par date de mise à jour (updatedAt) décroissante
        const dateA = a.updatedAt ? new Date(a.updatedAt.seconds * 1000) : new Date(0);
        const dateB = b.updatedAt ? new Date(b.updatedAt.seconds * 1000) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 1); // Prendre seulement le plus récent
  };

  // Formater la date
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Fonction pour obtenir l'icône appropriée pour une application en fonction de sa catégorie
  const getAppIcon = (app: ExtendedCustomerApp): React.ReactNode => {
    switch (app.category) {
      case 'webApp':
        return <WebIcon />;
      case 'api':
        return <CloudIcon />;
      case 'desktop':
        return <StorageIcon />;
      case 'mobileApp':
        return <PhoneIcon />;
      default:
        return <AppsIcon />;
    }
  };

  // Fonction pour transformer les applications avec des informations supplémentaires
  const formatApps = (apps: ExtendedCustomerApp[]): Application[] => {
    return apps.map(app => ({
      id: app.id,
      name: app.name,
      description: app.description || '',
      status: app.status as 'active' | 'pending' | 'expired' | 'inactive' | 'development' | 'maintenance',
      lastUsed: app.lastUsed,
      icon: getAppIcon(app),
      urlApp: app.urlApp || '',
      category: app.category || '',
      techStack: app.techStack || '',
      stacks: app.stacks || [],
      link: app.link || '',
      logoUrl: app.logoUrl || '',
      downloads: app.downloads || []
    }));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        // Récupérer le profil utilisateur depuis Firebase
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
        
        // Si l'utilisateur est associé à un client, récupérer les informations du client
        if (profile?.customerId) {
          // Récupérer les informations du client
          const customer = await getCustomer(profile.customerId);
          setCustomerData(customer);
          
          // Récupérer les adresses du client
          const addresses = await getCustomerAddresses(profile.customerId);
          setCustomerAddresses(addresses);
          
          // Récupérer les applications du client
          const customerApps = await getCustomerApps(profile.customerId) as ExtendedCustomerApp[];
          
          // Transformer les applications pour correspondre à notre interface Application
          const formattedApps = formatApps(customerApps);
          
          setApplications(formattedApps);
          
          // Simuler les données de facturation en fonction des informations du client
        setBilling({
          currentPlan: profile?.companySize === 'enterprise' ? 'Enterprise' : 'Professional',
          nextBilling: new Date(Date.now() + 2592000000).toISOString(), // Dans 30 jours
          amount: '€499.99',
          status: 'Active'
        });
        } else {
          // Pas de client associé, utiliser des données vides
          setApplications([]);
          setCustomerAddresses([]);
        }
        
        // Récupérer les tickets depuis Firebase
        const userTickets = await getTickets({ userId: currentUser.uid });
        setTickets(userTickets);
        
        // Calculer les statistiques de tickets
        const stats = calculateTicketStats(userTickets);
        setTicketStats(stats);
        
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography ml={2}>{getTranslationString('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 5, px: 2 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(90deg, #015480, #0288d1)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <DashboardIcon sx={{ fontSize: 32 }} />
            {getTranslationString('dashboard.client.title', 'Client Dashboard')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getTranslationString('dashboard.client.welcome', 'Welcome')} {currentUser?.displayName}! {getTranslationString('dashboard.client.subtitle', 'Manage your support tickets and account from here.')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Première colonne - Informations entreprise et utilisateur */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: '30%' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Cellule 1: Informations de l'entreprise */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                flex: 1
              }}
            >
                <Typography 
                  variant="h6" 
                  sx={{ 
                  mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                <BusinessIcon />
                {getTranslationString('settingclient.company.title', 'Company Information')}
                </Typography>
              
              {/* Logo et informations de l'entreprise */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                <Avatar 
                  src={customerData?.logoUrl || userProfile?.url_compagny_av} 
                  variant="rounded"
                  sx={{ 
                    width: 120, 
                    height: 120,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    mr: 3
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 60 }} />
                </Avatar>
                
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {getTranslationString('settingclient.company.companyName', 'Company Name')}
                      </Typography>
                    <Typography variant="h6">
                      {customerData?.companyName || userProfile?.companyName || '-'}
                      </Typography>
              </Box>
              
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {getTranslationString('settingclient.company.industry', 'Industry')}
                      </Typography>
                      <Typography variant="body1">
                        {customerData?.industry || userProfile?.industry || '-'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {getTranslationString('settingclient.company.companySize', 'Company Size')}
                      </Typography>
                      <Typography variant="body1">
                        {customerData?.companySize || userProfile?.companySize || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon fontSize="small" />
                {getTranslationString('settingclient.company.phone', 'Phone Number')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                {customerData?.phone || userProfile?.phone || getTranslationString('common.noData', 'No phone number available')}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" />
                {getTranslationString('settingclient.company.email', 'Email')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                {customerData?.email || userProfile?.companyEmail || getTranslationString('common.noData', 'No email available')}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon fontSize="small" />
                {getTranslationString('settingclient.address.title', 'Address')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                {customerAddresses.length > 0 ? (
                  <>
                    {/* Chercher d'abord une adresse principale (isMain) */}
                    {(() => {
                      // Trouver l'adresse principale
                      const mainAddress = customerAddresses.find(addr => addr.isMain);
                      // Ou utiliser la première adresse de siège social
                      const headquarterAddress = customerAddresses.find(addr => addr.category === 'siege');
                      // Ou simplement la première adresse
                      const addressToShow = mainAddress || headquarterAddress || customerAddresses[0];
                      
                      return (
                        <>
                          {addressToShow.category && (
                            <Typography variant="caption" display="block" fontWeight="bold" color="primary.main" sx={{ mb: 0.5 }}>
                              {addressToShow.category}
                            </Typography>
                          )}
                          {addressToShow.street}<br />
                          {addressToShow.city}, {addressToShow.postalCode}<br />
                          {addressToShow.country}
                        </>
                      );
                    })()}
                  </>
                ) : customerData?.addresses && customerData.addresses.length > 0 ? (
                  <>
                    {customerData.addresses[0].street}<br />
                    {customerData.addresses[0].city}, {customerData.addresses[0].postalCode}<br />
                    {customerData.addresses[0].country}
                  </>
                ) : userProfile?.address ? (
                  <>
                    {userProfile.address}<br />
                    {userProfile.city}, {userProfile.state} {userProfile.zipCode}<br />
                    {userProfile.country}
                  </>
                ) : (
                  getTranslationString('common.noData', 'No address information available')
                )}
              </Typography>
            </Paper>
          
            {/* Cellule 2: Informations de l'utilisateur */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                flex: 1
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <AccountCircleIcon />
                {getTranslationString('settingclient.personal.title', 'Personal Information')}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  src={userProfile?.avatarUrl}
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    mr: 2 
                  }}
                >
                  {!userProfile?.avatarUrl && <AccountCircleIcon sx={{ fontSize: 50 }} />}
                </Avatar>
                <Box>
                  <Typography variant="h6">{userProfile?.displayName || currentUser?.displayName || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userProfile?.jobTitle || '-'}
                  </Typography>
                </Box>
              </Box>
              
              <List dense>
                <ListItem>
                    <ListItemIcon>
                    <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                    primary={getTranslationString('settingclient.personal.email', 'Email Address')}
                    secondary={currentUser?.email}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                    <PhoneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                    primary={getTranslationString('settingclient.personal.phoneNumber', 'Phone Number')}
                    secondary={userProfile?.phoneNumber || '-'}
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  component={Link} 
                  to="/settings" 
                  variant="outlined" 
                  size="small" 
                  fullWidth
                  startIcon={<AccountCircleIcon />}
                >
                  {getTranslationString('nav.settings', 'Settings')}
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Deuxième colonne - Applications et statistiques */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: '67%' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Applications */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <AppsIcon />
                {getTranslationString('myhub.applications.title', 'Your Applications')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
                {applications.map((app, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      width: 250,
                      height: 250,
                      m: 0.5
                    }}
                  >
                    <Card 
                      sx={{ 
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)'
                        },
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'
                      }}
                    >
                      {/* Logo en arrière-plan */}
                      {(app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backdropFilter: 'blur(6px)',
                              zIndex: 2
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)',
                              zIndex: 1
                            }
                          }}
                        >
                          <Box
                            component="img"
                            src={app.logoUrl || app.urlApp}
                            alt={app.name}
                            sx={{
                              width: '120%',
                                                              height: '120%',
                                objectFit: 'cover',
                                opacity: 0.5,
                                filter: 'blur(5px) saturate(1.2)',
                                transform: 'scale(1.1)',
                              animation: 'slowZoom 30s infinite alternate ease-in-out',
                              '@keyframes slowZoom': {
                                '0%': { transform: 'scale(1.1)' },
                                '100%': { transform: 'scale(1.3)' }
                              }
                            }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </Box>
                                              ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              background: 'linear-gradient(145deg, #f0f4ff 0%, #e0e8ff 100%)',
                              zIndex: 0,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%'
                              }}
                            >
                              {/* Cercles décoratifs en arrière-plan */}
                              <Box sx={{
                                position: 'absolute',
                                width: '150%',
                                height: '150%',
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.2) 100%)',
                                top: '-25%',
                                left: '-25%',
                                animation: 'rotate 30s linear infinite',
                                '@keyframes rotate': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' }
                                }
                              }} />
                              <Box sx={{
                                position: 'absolute',
                                width: '120%',
                                height: '120%',
                                borderRadius: '50%',
                                border: '8px dashed rgba(25, 118, 210, 0.1)',
                                top: '-10%',
                                left: '-10%',
                                animation: 'rotate 50s linear infinite reverse',
                              }} />
                              
                              {/* Icône principale */}
                              <Box sx={{ 
                                fontSize: '8rem', 
                                color: 'primary.main',
                                opacity: 0.5,
                                zIndex: 1,
                                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))',
                                animation: 'pulse 4s infinite alternate ease-in-out',
                                '@keyframes pulse': {
                                  '0%': { transform: 'scale(1)' },
                                  '100%': { transform: 'scale(1.1)' }
                                }
                              }}>
                            {app.icon}
                              </Box>
                            </Box>
                          </Box>
                        )}
                      
                      {/* Statut déplacé au contenu de la carte */}
                      
                      {/* Contenu de la carte */}
                      <Box sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        p: 2, 
                        position: 'relative',
                        zIndex: 2,
                        justifyContent: 'space-between',
                        height: '100%',
                        color: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'white' : 'inherit'
                      }}>
                        <Box>
                          <Box sx={{ mb: 1 }}>
                            <Typography 
                              variant="h5" 
                              fontWeight="bold" 
                              noWrap 
                              sx={{ 
                                maxWidth: '100%',
                                mb: 1,
                                textShadow: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? '0px 1px 2px rgba(0,0,0,0.8)' : 'none',
                                background: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) 
                                  ? 'linear-gradient(45deg, #f5f7fa 0%, #c3cfe2 100%)'
                                  : 'linear-gradient(120deg, #2196f3 0%, #0d47a1 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                                WebkitTextFillColor: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'white' : 'transparent'
                              }}
                            >
                            {app.name}
                          </Typography>
                        </Box>
                          
                                                    {/* Description de l'application */}
                          {app.description && (
                            <Box sx={{ mb: 2.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.85rem',
                                  color: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 4,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  lineHeight: 1.4,
                                  mt: 1
                                }}
                              >
                                {app.description.length > 150 ? app.description.substring(0, 150) + '...' : app.description}
                        </Typography>
                            </Box>
                          )}
                        </Box>
                        
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Box
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                color: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'white' : 'primary.main'
                              }}
                            >
                              {/* Afficher l'icône de catégorie */}
                              {app.icon}
                            </Box>
                            {app.category && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.7rem',
                                  color: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                                }}
                              >
                                {app.category}
                              </Typography>
                            )}
                            
                            {/* Dernière utilisation */}
                            {app.lastUsed && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  ml: 'auto',
                                  fontSize: '0.65rem',
                                  color: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                }}
                              >
                                {new Date(app.lastUsed).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Statut et plateformes disponibles sur la même ligne */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                            {/* Statut de l'application */}
                        <Chip 
                              label={getTranslationString(`common.${app.status}`, getStatusLabel(app.status))}
                          size="small" 
                              color={getStatusColor(app.status) as any}
                              sx={{ 
                                fontWeight: 'medium',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            />
                            
                            {/* Plateformes disponibles */}
                            {app.downloads && app.downloads.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.7 }}>
                                {app.downloads.map((download, idx) => (
                                  <Tooltip key={idx} title={`${download.platform} v${download.version || '1.0'}`}>
                                    <Box
                                      component="a"
                                      href={download.downloadUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        bgcolor: (app.logoUrl || (app.urlApp && isImageUrl(app.urlApp))) ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                          transform: 'scale(1.2)'
                                        },
                                        textDecoration: 'none'
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={getPlatformLogo(download.platform)}
                                        alt={download.platform}
                                        sx={{
                                          width: 18,
                                          height: 18,
                                          objectFit: 'contain'
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button 
                  component={Link}
                  to="/myhub"
                  variant="outlined" 
                  startIcon={<AppsIcon />}
                >
                  {getTranslationString('myhub.applications.viewAll', 'View All Applications')}
                </Button>
              </Box>
            </Paper>
            
            {/* Statistiques et facturation */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {/* Facturation */}
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1.5rem)' } }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3,
                display: 'flex',
                alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <PaymentIcon />
                    {getTranslationString('dashboard.client.billing', 'Billing Overview')}
                  </Typography>
                  
                  {billing && (
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{getTranslationString('dashboard.client.currentPlan', 'Current Plan')}</Typography>
                        <Typography fontWeight="bold">{billing.currentPlan}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{getTranslationString('dashboard.client.nextBilling', 'Next Billing Date')}</Typography>
                        <Typography>{new Date(billing.nextBilling).toLocaleDateString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{getTranslationString('dashboard.client.amount', 'Amount')}</Typography>
                        <Typography color="primary.main" fontWeight="bold">{billing.amount}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{getTranslationString('dashboard.client.status', 'Status')}</Typography>
                        <Chip 
                          label={billing.status} 
                          size="small" 
                          color="success"
                        />
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        {getTranslationString('dashboard.client.viewBillingHistory', 'View Billing History')}
                      </Button>
                    </Stack>
                  )}
                </Paper>
              </Box>
              
              {/* Tickets */}
              <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(50% - 1.5rem)' } }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <ConfirmationNumberIcon />
                      {getTranslationString('supportclient.tickets.title', 'Support Tickets')}
                    </Typography>
                  </Box>
                  
                  {ticketStats && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(20% - 1.6rem)' } }}>
                        <Card sx={{ 
                          bgcolor: 'primary.light', 
                          color: 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          height: '80px',
                          p: 0
                        }}>
                          <CardContent sx={{ 
                            textAlign: 'center', 
                            p: '8px',
                            '&:last-child': { pb: '8px' }
                          }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {ticketStats.active}
                            </Typography>
                            <Typography variant="body2">{getTranslationString('dashboard.client.activeTickets', 'Active')}</Typography>
                          </CardContent>
                        </Card>
                      </Box>
                      
                      <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(20% - 1.6rem)' } }}>
                        <Card sx={{ 
                          bgcolor: 'success.light', 
                          color: 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          height: '80px',
                          p: 0
                        }}>
                          <CardContent sx={{ 
                            textAlign: 'center', 
                            p: '8px',
                            '&:last-child': { pb: '8px' }
                          }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {ticketStats.resolved}
                            </Typography>
                            <Typography variant="body2">{getTranslationString('dashboard.client.resolvedTickets', 'Resolved')}</Typography>
                          </CardContent>
                        </Card>
                      </Box>
                      
                      <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(20% - 1.6rem)' } }}>
                        <Card sx={{ 
                          bgcolor: 'warning.light', 
                          color: 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          height: '80px',
                          p: 0
                        }}>
                          <CardContent sx={{ 
                            textAlign: 'center', 
                            p: '8px',
                            '&:last-child': { pb: '8px' }
                          }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {ticketStats.pending}
                            </Typography>
                            <Typography variant="body2">{getTranslationString('dashboard.client.pendingTickets', 'Pending')}</Typography>
                          </CardContent>
                        </Card>
                      </Box>

                      <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(20% - 1.6rem)' } }}>
                        <Card sx={{ 
                          bgcolor: 'error.light', 
                          color: 'white',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          height: '80px',
                          p: 0
                        }}>
                          <CardContent sx={{ 
                            textAlign: 'center', 
                            p: '8px',
                            '&:last-child': { pb: '8px' }
                          }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {ticketStats.closed}
                            </Typography>
                            <Typography variant="body2">{getTranslationString('supportclient.status.closed', 'Closed')}</Typography>
                          </CardContent>
                        </Card>
                      </Box>

                      <Box sx={{ flexGrow: 1, flexBasis: '100%', mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                          {getTranslationString('dashboard.client.recentTickets', 'Recent')}
                        </Typography>
                        
                        {tickets.length > 0 ? (
                          getRecentTickets(tickets).map((ticket) => (
                            <Card key={ticket.id} sx={{ mb: 2, p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {ticket.subject}
                                </Typography>
                                <Chip 
                                  size="small" 
                                  label={ticket.status} 
                                  color={
                                    ticket.status === 'new' ? 'info' :
                                    ticket.status === 'inProgress' ? 'primary' :
                                    ticket.status === 'pending' ? 'warning' :
                                    ticket.status === 'resolved' ? 'success' : 'default'
                                  }
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {ticket.description?.length > 100 
                                  ? ticket.description.substring(0, 100) + '...' 
                                  : ticket.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getTranslationString('supportclient.tickets.lastUpdated', 'Last updated')}: {formatDate(ticket.updatedAt)}
                              </Typography>
                            </Card>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography color="text.secondary">
                              {getTranslationString('dashboard.client.noActiveTickets', 'No active tickets at the moment')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button 
                      component={Link}
                      to="/support"
                      variant="outlined" 
                      startIcon={<ConfirmationNumberIcon />}
                    >
                      {getTranslationString('dashboard.client.viewAll', 'View All Tickets')}
                    </Button>
                  </Box>
                </Paper>
              </Box>
              
              {/* Statistiques supplémentaires */}
              <Box sx={{ flexGrow: 1, flexBasis: '100%' }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <AssessmentIcon />
                    {getTranslationString('dashboard.client.statistics', 'Usage Statistics')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(25% - 1.5rem)' } }}>
                      <Card sx={{ bgcolor: 'background.paper', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {getTranslationString('dashboard.client.totalStorage', 'Total Storage')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            24.5 GB
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getTranslationString('dashboard.client.of', 'of')} 50 GB
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                    
                    <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(25% - 1.5rem)' } }}>
                      <Card sx={{ bgcolor: 'background.paper', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {getTranslationString('dashboard.client.activeUsers', 'Active Users')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            12
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +3 {getTranslationString('dashboard.client.thisMonth', 'this month')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                    
                    <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(25% - 1.5rem)' } }}>
                      <Card sx={{ bgcolor: 'background.paper', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {getTranslationString('dashboard.client.uptime', 'Uptime')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            99.9%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getTranslationString('dashboard.client.last30Days', 'last 30 days')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                    
                    <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 'calc(25% - 1.5rem)' } }}>
                      <Card sx={{ bgcolor: 'background.paper', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {getTranslationString('dashboard.client.apiCalls', 'API Calls')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            1.2M
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getTranslationString('dashboard.client.thisMonth', 'this month')}
                          </Typography>
                        </CardContent>
                      </Card>
                </Box>
              </Box>
            </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default DashboardClient; 